import { useEffect } from "react";

/**
 * Props for the useOnClickOutside hook
 */
type UseOnClickOutsideProps = {
  /**
   * Callback when clicking outside targets
   */
  onOutsidePress?: (e: globalThis.MouseEvent) => void;
  /**
   * Callback when closing (after outside press)
   */
  onClose?: () => void;
  /**
   * Array of target elements to monitor
   */
  targets: (HTMLElement | undefined)[];
};

/**
 * Custom hook for detecting clicks outside of specified target elements.
 *
 * Features:
 * - Detects clicks outside of multiple target elements
 * - Supports both mousedown and mouseup event handling
 * - Provides separate callbacks for outside press and close actions
 * - Handles SSR environments gracefully
 * - Automatically cleans up event listeners
 * - Uses composedPath for accurate event target detection
 * - Supports optional callbacks for flexible usage
 *
 * @param props - Configuration object containing targets and optional callbacks
 * @returns void
 */
export const useOnClickOutside = ({
  onOutsidePress,
  targets,
  onClose,
}: UseOnClickOutsideProps) => {
  useEffect(() => {
    if (typeof document === "undefined") return;

    let isClosable = false;
    const onMouseDownHandler = (e: globalThis.MouseEvent) => {
      let isTarget = false;
      const path = e.composedPath();
      for (const el of targets) {
        if (el && path.includes(el)) {
          isTarget = true;
          break;
        }
      }

      if (!isTarget) {
        onOutsidePress?.(e);
        isClosable = true;
      }
    };
    const onMouseUpHandler = (e: globalThis.MouseEvent) => {
      if (!isClosable) return;
      let isTarget = false;
      const path = e.composedPath();
      for (const el of targets) {
        if (el && path.includes(el)) {
          isTarget = true;
          break;
        }
      }
      if (!isTarget) {
        onClose?.();
      }
    };

    document.addEventListener("mousedown", onMouseDownHandler);
    document.addEventListener("mouseup", onMouseUpHandler);
    return () => {
      document.removeEventListener("mousedown", onMouseDownHandler);
      document.removeEventListener("mouseup", onMouseUpHandler);
    };
  }, [onClose, onOutsidePress, targets]);
};
